import { useState } from "react";
import { toast } from "sonner";
import { Link2, Check, Share2 } from "lucide-react";

// Elegant editorial share row — X, Facebook, LinkedIn, Pinterest, WhatsApp, copy link + native share.
const enc = encodeURIComponent;

const SocialIcon = ({ name }) => {
  const common = { width: 16, height: 16, viewBox: "0 0 24 24", fill: "currentColor" };
  switch (name) {
    case "x":
      return (
        <svg {...common} aria-hidden>
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25h6.83l4.713 6.231zm-1.16 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      );
    case "facebook":
      return (
        <svg {...common} aria-hidden>
          <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.19 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.51 1.49-3.9 3.78-3.9 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.78-1.63 1.57v1.88h2.78l-.44 2.91h-2.34V22c4.78-.75 8.44-4.92 8.44-9.94z" />
        </svg>
      );
    case "linkedin":
      return (
        <svg {...common} aria-hidden>
          <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.63-1.85 3.36-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z" />
        </svg>
      );
    case "pinterest":
      return (
        <svg {...common} aria-hidden>
          <path d="M12 2C6.48 2 2 6.48 2 12c0 4.24 2.64 7.86 6.36 9.32-.09-.79-.17-2 .03-2.86.18-.78 1.17-4.97 1.17-4.97s-.3-.6-.3-1.48c0-1.39.8-2.42 1.8-2.42.85 0 1.26.64 1.26 1.4 0 .85-.54 2.13-.82 3.31-.24.99.5 1.8 1.48 1.8 1.77 0 3.13-1.87 3.13-4.57 0-2.39-1.72-4.06-4.17-4.06-2.84 0-4.51 2.13-4.51 4.33 0 .86.33 1.78.74 2.28.08.1.09.19.07.29-.08.32-.25 1-.28 1.14-.04.18-.15.22-.34.13-1.25-.58-2.03-2.4-2.03-3.87 0-3.15 2.29-6.04 6.6-6.04 3.47 0 6.16 2.47 6.16 5.77 0 3.44-2.17 6.22-5.19 6.22-1.01 0-1.97-.53-2.29-1.15l-.62 2.37c-.23.87-.83 1.96-1.24 2.62.93.29 1.92.44 2.95.44 5.52 0 10-4.48 10-10S17.52 2 12 2z" />
        </svg>
      );
    case "whatsapp":
      return (
        <svg {...common} aria-hidden>
          <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.06 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2-1.41.25-.69.25-1.28.17-1.41-.07-.13-.27-.2-.57-.35zM12 2C6.48 2 2 6.48 2 12c0 1.77.46 3.42 1.27 4.86L2 22l5.28-1.38A9.93 9.93 0 0 0 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2z" />
        </svg>
      );
    default:
      return null;
  }
};

export const ShareBar = ({ title, image, className = "" }) => {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const text = title || "Sybils & Society";

  const links = {
    x: `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(text)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`,
    pinterest: `https://pinterest.com/pin/create/button/?url=${enc(url)}&media=${enc(image || "")}&description=${enc(text)}`,
    whatsapp: `https://wa.me/?text=${enc(`${text} — ${url}`)}`,
  };

  const open = (href) => window.open(href, "_blank", "noopener,noreferrer,width=640,height=560");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied to your clipboard.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy the link.");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: text, url });
      } catch {
        /* user dismissed */
      }
    } else {
      copy();
    }
  };

  const Btn = ({ name, label, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="flex h-10 w-10 items-center justify-center rounded-full border border-[#0A0A0A]/15 text-[#0A0A0A]/60 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#C05C42] hover:bg-[#C05C42] hover:text-[#FAF9F6]"
      data-testid={`share-${name}-btn`}
    >
      {name === "copy" ? (
        copied ? <Check size={16} /> : <Link2 size={16} />
      ) : name === "native" ? (
        <Share2 size={16} />
      ) : (
        <SocialIcon name={name} />
      )}
    </button>
  );

  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`} data-testid="share-bar">
      <span className="font-body text-[0.6rem] uppercase tracking-[0.25em] text-[#0A0A0A]/40">Share</span>
      <div className="flex flex-wrap items-center gap-2">
        <Btn name="x" label="Share on X" onClick={() => open(links.x)} />
        <Btn name="facebook" label="Share on Facebook" onClick={() => open(links.facebook)} />
        <Btn name="linkedin" label="Share on LinkedIn" onClick={() => open(links.linkedin)} />
        <Btn name="pinterest" label="Save to Pinterest" onClick={() => open(links.pinterest)} />
        <Btn name="whatsapp" label="Share on WhatsApp" onClick={() => open(links.whatsapp)} />
        <Btn name="copy" label="Copy link" onClick={copy} />
        {typeof navigator !== "undefined" && navigator.share && (
          <Btn name="native" label="Share" onClick={nativeShare} />
        )}
      </div>
    </div>
  );
};

export default ShareBar;
